'use client';

import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { canonicalFilters, normalizeFilterParams, normalizeSearchSort } from '@/lib/search-filters';
import type { SearchExportOutcome } from '@/lib/search-export';
import { useSearchExport } from '@/hooks/use-search-export';
import type { FilterParams } from '@/lib/search-filter-params';
import { filterLocationsForCountry } from '@/new_components/searchresults/searchConstants';
import { useMarkOpened, useSavedSearches } from '@/hooks/use-saved-searches';
import { trackSearchPagination, trackSearchPerformed, trackSearchSortChange } from '@/lib/analytics';
import { exportLogBody, filtersToSearchParams, submittedFilterCount } from './searchPageFilters';
import type { NaceCode } from './searchPageTypes';
import type { SearchResultsState } from './useSearchResults';

type ExecuteSearch = (
  filters: FilterParams,
  overrides: { sort: string; offset: number; limit: number },
) => Promise<void>;

/**
 * Applying filters: the header's country selector and the sidebar's submit.
 * `lastFiltersRef` and `setFiltersOpen` are listed as dependencies only
 * because they arrive as arguments here; both are stable, so the callbacks
 * keep the identities they had before.
 */
export function useSearchSubmit(
  results: SearchResultsState,
  executeSearch: ExecuteSearch,
  searchParams: URLSearchParams,
  setFiltersOpen: Dispatch<SetStateAction<boolean>>,
) {
  const { lastFiltersRef, currentSort, currentLimit, searchResponse } = results;

  // The header's flag selector (LandingSearch) applies its choice here, so a
  // country change runs through the same client search path as any sidebar
  // filter — instant results, shallow URL update, sidebar state synced from
  // the URL. It acts on the filters behind the results on screen; staged
  // sidebar edits stay staged.
  const applyCountry = useCallback(
    (country: string) => {
      const prev = lastFiltersRef.current;
      if ((prev.country ?? '') === country) return;
      executeSearch(
        {
          ...prev,
          country,
          Bundesland: filterLocationsForCountry(prev.Bundesland, country),
          // Cities cannot be checked client-side (13.6K of them, country
          // known only in SQL), so narrowing to a specific country clears
          // them: "Switzerland + Hamburg" is a guaranteed empty result the
          // user did not ask for. Widening to global keeps them — every
          // selected city is still reachable there.
          city: country ? [] : prev.city,
        },
        { sort: currentSort, offset: 0, limit: currentLimit },
      );
    },
    [executeSearch, currentSort, currentLimit, lastFiltersRef],
  );

  useEffect(() => {
    const onSetCountry = (e: Event) =>
      applyCountry((e as CustomEvent<string>).detail ?? 'DE');
    window.addEventListener('firmendata:set-country', onSetCountry);
    return () =>
      window.removeEventListener('firmendata:set-country', onSetCountry);
  }, [applyCountry]);

  const handleSubmit = useCallback(
    (filters: FilterParams) => {
      const filterCount = submittedFilterCount(filters);
      trackSearchPerformed({
        query: filters.q,
        filterCount,
        resultCount: searchResponse.total,
      });

      const sortOverride =
        (filters.q || filters.personName) &&
        (currentSort === 'revenue-desc' || !searchParams.get('sort'))
          ? 'relevance'
          : currentSort;
      executeSearch(filters, {
        sort: sortOverride,
        offset: 0,
        limit: currentLimit,
      });

      if (typeof window !== 'undefined' && window.innerWidth < 1024) {
        setFiltersOpen(false);
      }
    },
    [
      executeSearch,
      searchResponse.total,
      currentSort,
      currentLimit,
      searchParams,
      setFiltersOpen,
    ],
  );

  return { applyCountry, handleSubmit };
}

/** Saved searches: save (which also applies), quick-load, and the dialog's state. */
export function useSavedSearchActions(
  results: SearchResultsState,
  executeSearch: ExecuteSearch,
  handleSubmit: (filters: FilterParams) => void,
  initialFilters: FilterParams,
  naceCodes: NaceCode[],
) {
  const { currentSort, currentLimit } = results;
  const { items: savedSearches } = useSavedSearches();
  const markSavedSearchOpened = useMarkOpened();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<FilterParams | null>(null);

  /**
   * Save applies the filters as well.
   *
   * The sidebar hands over its *staged* state, which need not match the
   * results on screen. Running the search as part of saving is what removes
   * the ambiguity — after this the visible results are the saved search,
   * and the dialog's chip preview showed exactly what was stored.
   */
  const handleSaveSearch = useCallback(
    (filters: FilterParams) => {
      handleSubmit(filters);
      setPendingSave(filters);
      setSaveDialogOpen(true);
    },
    [handleSubmit],
  );

  const handleLoadSavedSearch = useCallback(
    (id: number) => {
      const saved = savedSearches.find((s) => s.id === id);
      if (!saved) return;
      // The badge counts new matches since the user last *looked*, so it
      // only resets here — not when the card merely renders.
      markSavedSearchOpened(id);
      const filters = normalizeFilterParams(saved.filters);
      // `sort` holds this page's combined form ("revenue-desc"), the same
      // string executeSearch and the backend both already understand — so
      // sort_direction stays unused here rather than being re-derived.
      executeSearch(filters, {
        sort: normalizeSearchSort(
          saved.sort || currentSort,
          filters.q || filters.personName,
        ),
        offset: 0,
        limit: currentLimit,
      });
    },
    [savedSearches, markSavedSearchOpened, executeSearch, currentSort, currentLimit],
  );

  const savedSearchOptions = useMemo(
    () =>
      savedSearches.map((s) => ({
        id: s.id,
        name: s.name,
        filters: normalizeFilterParams(s.filters),
      })),
    [savedSearches],
  );

  const isCurrentSearchSaved = useMemo(() => {
    const current = canonicalFilters(
      initialFilters as unknown as Record<string, unknown>,
    );
    return savedSearches.some((s) => canonicalFilters(s.filters) === current);
  }, [savedSearches, initialFilters]);

  const pendingSaveQuery = useMemo(() => {
    if (!pendingSave) return null;
    return filtersToSearchParams(pendingSave, naceCodes, {
      sort: currentSort,
      offset: 0,
      limit: currentLimit,
    }) as unknown as Record<string, unknown>;
  }, [pendingSave, naceCodes, currentSort, currentLimit]);

  return {
    saveDialogOpen, setSaveDialogOpen, pendingSave, handleSaveSearch, handleLoadSavedSearch,
    savedSearchOptions, isCurrentSearchSaved, pendingSaveQuery,
  };
}

/** Re-running the current filters at another sort, page size, page or as-is. */
export function usePagingHandlers(
  results: SearchResultsState,
  executeSearch: ExecuteSearch,
  initialFilters: FilterParams,
) {
  const { currentSort, currentOffset, currentLimit } = results;
  const handleSortChange = useCallback(
    (newSort: string) => {
      trackSearchSortChange(newSort);
      executeSearch(initialFilters, {
        sort: newSort,
        offset: 0,
        limit: currentLimit,
      });
    },
    [
      executeSearch,
      initialFilters,
      currentLimit,
    ],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      executeSearch(initialFilters, {
        limit: newLimit,
        offset: 0,
        sort: currentSort,
      });
    },
    [
      executeSearch,
      initialFilters,
      currentSort,
    ],
  );

  const handleOffsetChange = useCallback(
    (newOffset: number) => {
      trackSearchPagination(newOffset);
      executeSearch(initialFilters, {
        offset: newOffset,
        sort: currentSort,
        limit: currentLimit,
      });
    },
    [
      executeSearch,
      initialFilters,
      currentSort,
      currentLimit,
    ],
  );

  const handleRefresh = useCallback(() => {
    executeSearch(initialFilters, {
      sort: currentSort,
      offset: currentOffset,
      limit: currentLimit,
    });
  }, [executeSearch, initialFilters, currentSort, currentOffset, currentLimit]);

  return { handleSortChange, handleLimitChange, handleOffsetChange, handleRefresh };
}

/**
 * The results table's side channels: the enrich overlay, the export re-fetch
 * and the export log. `setSearchResponse` and `lastFiltersRef` are listed as
 * dependencies only because they arrive as arguments here; both are stable.
 */
export function useResultsSideActions(
  results: SearchResultsState,
  naceCodes: NaceCode[],
  renderedSearchParams: string,
  searchParams: URLSearchParams,
  canExport: boolean,
  initialFilters: FilterParams,
) {
  const { setSearchResponse, lastFiltersRef, currentSort, searchResponse } = results;

  // Overlay freshly-enriched employee counts onto the displayed rows. The
  // counts come straight from the enrich response (parsed from employee_data);
  // mv_search is a materialized view we can't write per-click, so this is an
  // in-memory patch. A new search replaces results entirely, falling back to
  // the MV value until the next scheduled MV refresh.
  const handleEmployeeCountsUpdated = useCallback(
    (counts: Record<string, number>) => {
      if (Object.keys(counts).length === 0) return;
      setSearchResponse((prev) => ({
        ...prev,
        results: prev.results.map((r) =>
          counts[r.euId] != null ? { ...r, employees: counts[r.euId] } : r,
        ),
      }));
    },
    [setSearchResponse],
  );

  // Fetch up to `rows` rows for the Excel export, reusing the exact filters,
  // sort and financial year behind the currently displayed results. The
  // backend bills the export (1,000 credits) once it has produced the rows.
  // Returns the outcome only — it never touches the table state or the URL.
  const requestExport = useSearchExport();
  const handleFetchForExport = useCallback(
    (rows: number): Promise<SearchExportOutcome> => {
      const params = filtersToSearchParams(lastFiltersRef.current, naceCodes, {
        sort: currentSort,
        offset: 0,
        limit: rows,
      });
      return requestExport(params, rows);
    },
    [
      naceCodes,
      currentSort,
      lastFiltersRef,
      requestExport,
    ],
  );

  // Log Excel-export usage to /api/log/export so it surfaces on /admin/logs.
  // `stage` is 'click' (button pressed — any user, incl. guests and non-Pro
  // users, before the Pro gate) or 'completed' (file generated). The active
  // filters mirror the search-query log so admins can see what was exported.
  const logExport = useCallback(
    (stage: 'click' | 'completed', extra?: Record<string, unknown>) => {
      const url = `/search?${renderedSearchParams}`;
      const body = exportLogBody(stage, extra, url, canExport, searchParams, initialFilters, currentSort, { total: searchResponse.total });
      fetch('/api/log/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    },
    [
      searchParams,
      renderedSearchParams,
      canExport,
      initialFilters,
      currentSort,
      searchResponse.total,
    ],
  );

  return { handleEmployeeCountsUpdated, handleFetchForExport, logExport };
}
